import React from 'react';
import {
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  Box,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  FormControlLabel,
  ListItemText,
} from '@mui/material';
import { useForm, Controller } from 'react-hook-form';
import { useWarbandStore } from '../../stores/warbandStore';
import { useDirtyTrackingStore } from '../../stores/dirtyTrackingStore';
import { useReferenceDataStore } from '../../stores/referenceDataStore';

interface CreateApprenticeFormData {
  name: string;
  weaponIds: string[];
  armourIds: string[];
}

export const CreateApprenticeModal: React.FC = () => {
  const {
    ui,
    closeCreateApprenticeModal,
  } = useWarbandStore();

  const [error, setError] = React.useState<string | null>(null);
  const [isFree, setIsFree] = React.useState(false);
  const weapons = useReferenceDataStore((s) => s.weapons);
  const { control, handleSubmit, reset } = useForm<CreateApprenticeFormData>({
    defaultValues: { name: '', weaponIds: [], armourIds: [] },
  });

  const onSubmit = (data: CreateApprenticeFormData) => {
    const selectedWeapons = weapons.filter((w) => data.weaponIds.includes(w.id));
    useDirtyTrackingStore.getState().queueApprenticeCreate(data.name, selectedWeapons, isFree);
    reset();
    setIsFree(false);
    closeCreateApprenticeModal();
  };

  const handleClose = () => {
    reset();
    setError(null);
    setIsFree(false);
    closeCreateApprenticeModal();
  };

  return (
    <Dialog
      open={ui.createApprenticeModalOpen}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
        {error && (
          <Box sx={{ color: '#d32f2f', fontSize: '0.875rem', p: 1, backgroundColor: '#ffebee', borderRadius: 1 }}>
            {error}
          </Box>
        )}

        <Controller
          name="name"
          control={control}
          rules={{ required: 'Apprentice name is required' }}
          render={({ field, fieldState: { error: fieldError } }) => (
            <TextField
              {...field}
              label="Apprentice Name"
              fullWidth
              error={!!fieldError}
              helperText={fieldError?.message}
            />
          )}
        />

        <Controller
          name="weaponIds"
          control={control}
          render={({ field }) => (
            <FormControl fullWidth>
              <InputLabel id="create-apprentice-weapons-label">Weapons</InputLabel>
              <Select
                {...field}
                labelId="create-apprentice-weapons-label"
                label="Weapons"
                multiple
                renderValue={(selected) => {
                  const selectedIds = selected as string[];
                  return weapons
                    .filter((weapon) => selectedIds.includes(weapon.id))
                    .map((weapon) => weapon.name)
                    .join(', ');
                }}
              >
                {weapons.map((weapon) => (
                  <MenuItem key={weapon.id} value={weapon.id}>
                    <Checkbox checked={field.value.includes(weapon.id)} />
                    <ListItemText primary={weapon.name} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
        />
      </DialogContent>
      <DialogActions>
        <FormControlLabel
          control={
            <Checkbox
              checked={isFree}
              onChange={(e) => setIsFree(e.target.checked)}
              size="small"
            />
          }
          label="Add for free"
          sx={{ mr: 'auto' }}
        />
        <Button onClick={handleClose}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit(onSubmit)}
          variant="contained"
        >
          {'Create Apprentice'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
